import type { PdfSearchMatch } from "./pdfSearch";

import { scrollingAncestor } from "./pageClip";

const pageSelector = (pageIndex: number) => `[data-page-index="${pageIndex}"]`;

/** Scrolls the page's top to the top of the preview's own scroller, leaving any outer one put. */
export function scrollToPage(viewport: HTMLElement | null, pageIndex: number) {
  const wrapper = viewport?.querySelector<HTMLElement>(pageSelector(pageIndex));
  if (viewport == null || wrapper == null) return;
  const scroller = scrollingAncestor(viewport);
  if (scroller == null) {
    // Nothing between the viewport and the window scrolls, so there is no ancestor to disturb.
    wrapper.scrollIntoView({ block: "start", behavior: "auto" });
    return;
  }
  scroller.scrollTop += wrapper.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
}

/** Centres a target vertically within the preview's own scroller only. */
function centreInViewport(viewport: HTMLElement, target: HTMLElement) {
  const scroller = scrollingAncestor(viewport);
  if (scroller == null) {
    target.scrollIntoView({ block: "center", behavior: "auto" });
    return;
  }
  const targetRect = target.getBoundingClientRect();
  const scrollerRect = scroller.getBoundingClientRect();
  scroller.scrollTop +=
    targetRect.top - scrollerRect.top - (scroller.clientHeight - targetRect.height) / 2;
}

/** The last page whose top has passed a third of the way down the viewport. */
export function pageInView(viewport: HTMLElement): number {
  const threshold = viewport.getBoundingClientRect().top + viewport.clientHeight / 3;
  const wrappers = viewport.querySelectorAll<HTMLElement>("[data-page-index]");
  // Wrappers sit in page order with increasing tops, so binary-search the last one above the line.
  let lo = 0;
  let hi = wrappers.length - 1;
  let page = 0;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (wrappers[mid].getBoundingClientRect().top < threshold) {
      page = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return page;
}

/** Frames to wait for the active match's page to draw its text before giving up. */
const MARK_WAIT_FRAMES = 60;

/**
 * Brings a search match into view: its page first, then the match itself once the
 * page's text layer has drawn it, which happens asynchronously after the page nears.
 */
export function revealMatch(viewport: HTMLElement, match: PdfSearchMatch): () => void {
  scrollToPage(viewport, match.pageIndex);
  let frames = 0;
  let handle = 0;
  const look = () => {
    const mark = viewport.querySelector<HTMLElement>('mark[data-active="true"]');
    if (mark != null) {
      centreInViewport(viewport, mark);
      return;
    }
    frames += 1;
    if (frames < MARK_WAIT_FRAMES) handle = requestAnimationFrame(look);
  };
  handle = requestAnimationFrame(look);
  return () => cancelAnimationFrame(handle);
}
